// dependencies
import React, { useCallback, useState, useRef } from "react";
import { useIntl } from "react-intl";
import {
  ScrollView,
  View,
  StyleSheet,
  TouchableWithoutFeedback,
} from "react-native";

// components
import { Row, Col } from "@cloudspire/legacy-resources/src/components/Grid";
import {
  List,
  ListItem,
} from "@cloudspire/legacy-resources/src/components/List";
import Text from "@cloudspire/legacy-resources/src/components/Text";
import Image from "@cloudspire/legacy-resources/src/components/Image";
import Stack from "@cloudspire/legacy-resources/src/components/Stack";
import Portal from "@cloudspire/legacy-resources/src/components/Portal";
import Carousel from "@cloudspire/legacy-resources/src/components/Carousel";
import Button from "@cloudspire/legacy-resources/src/components/Button2";
import Modal, {
  ModalBody,
  ModalHeader,
  ModalContent,
} from "@cloudspire/legacy-resources/src/components/Modal";
import {
  CarouselLeftNavigation,
  CarouselRightNavigation,
} from "@cloudspire/legacy-resources/src/components/RoomPictureListCarousel";

// libraries
import { emToPx } from "@cloudspire/legacy-resources/src/libraries";

// hocs
import useViewportMedia from "@cloudspire/legacy-resources/src/hocs/useViewportMedia";

type PropsType = {
  pictureList: Array<{ urls: any; caption: string }>;
  altFallback?: string;
};

const styles = StyleSheet.create({
  modalContent: {
    padding: emToPx(1),
  },
  buttonWrapper: {
    position: "absolute",
    width: "80%",
    top: "50%",
    left: "50%",
    transform: [{ translateX: "-50%" }, { translateY: "-50%" }] as any,
  },
  image: {
    cursor: "pointer",
  } as any,
});

const RoomPictureList: React.FunctionComponent<PropsType> = (props) => {
  const { altFallback, pictureList } = props;

  const intl = useIntl();

  const $carousel = useRef();

  const { matches } = useViewportMedia({
    queries: [
      {
        minWidth: 800,
      },
    ],
  });

  const [modalOpened, setModalOpened] = useState(false);

  const handleClickOpenGallery = useCallback((page) => {
    $carousel.current.setPage({ newActiveIndex: page });
    setModalOpened(true);
  }, []);

  if (pictureList.length === 0) {
    return null;
  }

  return (
    <>
      {matches ? (
        <>
          <Portal selector="body">
            <Modal
              opened={modalOpened}
              onShouldClose={() => setModalOpened(false)}
            >
              {(shouldClose) => (
                <ModalBody>
                  <View style={{ minWidth: "100vw" }} />

                  <ModalHeader onShouldClose={shouldClose}>
                    <Text style={{ color: "#ffffff" }}>
                      {intl.formatMessage({
                        defaultMessage: "Galerie photo",
                      })}
                    </Text>
                  </ModalHeader>

                  <ModalContent>
                    <View style={styles.modalContent}>
                      <Carousel
                        ref={$carousel}
                        shouldAutoplay={modalOpened}
                        autoplayOptions={{ delay: 5000 }}
                        leftNavigation={<CarouselLeftNavigation />}
                        rightNavigation={<CarouselRightNavigation />}
                      >
                        {pictureList.map((picture, index) => {
                          return (
                            <View key={index}>
                              <Image
                                ratio={3 / 4}
                                source={{ uri: picture.urls.large }}
                                accessibilityLabel={
                                  picture.caption ?? altFallback
                                }
                              />

                              {picture.caption && (
                                <>
                                  <Stack marginTop={1} />

                                  <Text>{picture.caption}</Text>
                                </>
                              )}
                            </View>
                          );
                        })}
                      </Carousel>
                    </View>
                  </ModalContent>
                </ModalBody>
              )}
            </Modal>
          </Portal>

          {pictureList.length === 1 && (
            <Row
              breakpointList={{ sm: 1100, md: Infinity }}
              col={{ sm: 1, md: 2 }}
            >
              <Col>
                <View style={{ position: "relative" }}>
                  <TouchableWithoutFeedback
                    onPress={() => handleClickOpenGallery(4)}
                  >
                    <View style={styles.image}>
                      <Image
                        ratio={3 / 4}
                        resizeMode="cover"
                        source={{ uri: pictureList[0].urls.large }}
                        accessibilityLabel={
                          pictureList[0].caption ?? altFallback
                        }
                      />
                    </View>
                  </TouchableWithoutFeedback>

                  <View style={styles.buttonWrapper}>
                    <Button
                      label={intl.formatMessage({
                        defaultMessage: "Voir plus",
                      })}
                      onPress={() => handleClickOpenGallery(0)}
                    />
                  </View>
                </View>
              </Col>
            </Row>
          )}

          {pictureList.length >= 2 && pictureList.length <= 4 && (
            <Row
              breakpointList={{ sm: 1100, md: Infinity }}
              col={{ sm: 1, md: 2 }}
            >
              <Col>
                <TouchableWithoutFeedback
                  onPress={() => handleClickOpenGallery(0)}
                >
                  <View style={styles.image}>
                    <Image
                      ratio={3 / 4}
                      resizeMode="cover"
                      source={{ uri: pictureList[0].urls.large }}
                      accessibilityLabel={pictureList[0].caption ?? altFallback}
                    />
                  </View>
                </TouchableWithoutFeedback>
              </Col>

              <Col>
                <View style={{ position: "relative" }}>
                  <TouchableWithoutFeedback
                    onPress={() => handleClickOpenGallery(4)}
                  >
                    <View style={styles.image}>
                      <Image
                        ratio={3 / 4}
                        resizeMode="cover"
                        source={{ uri: pictureList[1].urls.large }}
                        accessibilityLabel={
                          pictureList[1].caption ?? altFallback
                        }
                      />
                    </View>
                  </TouchableWithoutFeedback>

                  <View style={styles.buttonWrapper}>
                    <Button
                      label={intl.formatMessage({
                        defaultMessage: "Voir plus",
                      })}
                      onPress={() => handleClickOpenGallery(0)}
                    />
                  </View>
                </View>
              </Col>
            </Row>
          )}

          {pictureList.length >= 5 && (
            <Row
              breakpointList={{ sm: 1100, md: Infinity }}
              col={{ sm: 1, md: 2 }}
            >
              <Col>
                <TouchableWithoutFeedback
                  onPress={() => handleClickOpenGallery(0)}
                >
                  <View style={styles.image}>
                    <Image
                      ratio={3 / 4}
                      resizeMode="cover"
                      source={{ uri: pictureList[0].urls.large }}
                      accessibilityLabel={pictureList[0].caption ?? altFallback}
                    />
                  </View>
                </TouchableWithoutFeedback>
              </Col>

              <Col>
                <Row breakpointList={{ all: Infinity }} col={{ all: 2 }}>
                  <Col>
                    <TouchableWithoutFeedback
                      onPress={() => handleClickOpenGallery(1)}
                    >
                      <View style={styles.image}>
                        <Image
                          ratio={3 / 4}
                          resizeMode="cover"
                          source={{ uri: pictureList[1].urls.large }}
                          accessibilityLabel={
                            pictureList[1].caption ?? altFallback
                          }
                        />
                      </View>
                    </TouchableWithoutFeedback>
                  </Col>

                  <Col>
                    <TouchableWithoutFeedback
                      onPress={() => handleClickOpenGallery(2)}
                    >
                      <View style={styles.image}>
                        <Image
                          ratio={3 / 4}
                          resizeMode="cover"
                          source={{ uri: pictureList[2].urls.large }}
                          accessibilityLabel={
                            pictureList[2].caption ?? altFallback
                          }
                        />
                      </View>
                    </TouchableWithoutFeedback>
                  </Col>
                </Row>

                <Row breakpointList={{ all: Infinity }} col={{ all: 2 }}>
                  <Col>
                    <TouchableWithoutFeedback
                      onPress={() => handleClickOpenGallery(3)}
                    >
                      <View style={styles.image}>
                        <Image
                          ratio={3 / 4}
                          resizeMode="cover"
                          source={{ uri: pictureList[3].urls.large }}
                          accessibilityLabel={
                            pictureList[3].caption ?? altFallback
                          }
                        />
                      </View>
                    </TouchableWithoutFeedback>
                  </Col>

                  <Col>
                    <View style={{ position: "relative" }}>
                      <TouchableWithoutFeedback
                        onPress={() => handleClickOpenGallery(4)}
                      >
                        <View style={styles.image}>
                          <Image
                            ratio={3 / 4}
                            resizeMode="cover"
                            source={{ uri: pictureList[4].urls.large }}
                            accessibilityLabel={
                              pictureList[4].caption ?? altFallback
                            }
                          />
                        </View>
                      </TouchableWithoutFeedback>

                      <View style={styles.buttonWrapper}>
                        <Button
                          label={intl.formatMessage({
                            defaultMessage: "Voir plus",
                          })}
                          onPress={() => handleClickOpenGallery(0)}
                        />
                      </View>
                    </View>
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
        </>
      ) : (
        <ScrollView
          scrollEnabled={true}
          horizontal={true}
          style={{ paddingBottom: emToPx(1) }}
        >
          <List inline={true} gutter={0.625} noWrap={true}>
            {pictureList.map((picture, index) => {
              return (
                <ListItem
                  key={index}
                  style={{
                    flexBasis: 0,
                    flexShrink: 0,
                    flexGrow: 1,
                    minWidth: emToPx(15.625),
                    maxWidth: "100vw",
                  }}
                >
                  <View>
                    <Image
                      ratio={3 / 4}
                      source={{ uri: picture.urls.large }}
                      accessibilityLabel={picture.caption ?? altFallback}
                    />

                    {picture.caption && (
                      <>
                        <Stack marginTop={1} />

                        <Text>{picture.caption}</Text>
                      </>
                    )}
                  </View>
                </ListItem>
              );
            })}
          </List>
        </ScrollView>
      )}
    </>
  );
};

export default RoomPictureList;
